import React, { useState, useRef, useCallback } from 'react';
import WavesurferPlayer from '@wavesurfer/react';

const Voice = ({ previewUrl, authToken = null, voiceId = null, isCloned = false }) => {
  const [wavesurfer, setWavesurfer]     = useState(null);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [isReady, setIsReady]           = useState(false);   // true once WaveSurfer has decoded
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError]               = useState(null);
  const [audioUrl, setAudioUrl]         = useState(null);
  const [showPlayer, setShowPlayer]     = useState(false);

  const blobRef = useRef(null);

  // ── Generate TTS preview (cloned voices) ────────────────────
  const generatePreview = useCallback(async () => {
    if (!voiceId || !authToken) return;

    // Reset everything so WaveSurfer remounts cleanly
    setShowPlayer(false);
    setAudioUrl(null);
    setWavesurfer(null);
    setIsReady(false);
    setIsPlaying(false);
    setError(null);
    setIsGenerating(true);

    const sampleText = "You are using Alchemy Mix. This is a short voice demo so you can hear the tone, pacing, and clarity in a natural conversational style, with steady delivery, balanced sentences, and a relaxed rhythm that sounds human and easy to listen to";
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    try {
      const response = await fetch(`${apiBase}/speechify/tts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: sampleText,
          voice_id: voiceId,
          audio_format: 'mp3',
        }),
      });

      if (!response.ok) throw new Error(`Server error ${response.status}`);

      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error('Empty audio response');

      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
      const objUrl = URL.createObjectURL(blob);
      blobRef.current = objUrl;

      // Set URL first, then show player on next tick so React flushes the
      // showPlayer=false before remounting WaveSurfer with the new URL
      setAudioUrl(objUrl);
      setTimeout(() => setShowPlayer(true), 0);

    } catch (err) {
      console.error('TTS preview failed:', err);
      setError(err.message || 'Preview generation failed');
    } finally {
      setIsGenerating(false);
    }
  }, [voiceId, authToken]);

  // ── Load shared voice preview ────────────────────────────────
  const handlePlayShared = useCallback(() => {
    if (!previewUrl) return;
    setAudioUrl(previewUrl);
    setShowPlayer(true);
  }, [previewUrl]);

  // ── WaveSurfer callbacks ─────────────────────────────────────
  const onReady = useCallback((ws) => {
    setWavesurfer(ws);
    setIsReady(true);
    setIsPlaying(false);
    setTimeout(() => {
      try { ws.play(); } catch (e) { /* autoplay blocked — user must click play */ }
    }, 150);
  }, []);

  const onPlay   = useCallback(() => setIsPlaying(true),  []);
  const onPause  = useCallback(() => setIsPlaying(false), []);
  const onFinish = useCallback(() => setIsPlaying(false), []);

  const handlePlayPause = useCallback(() => {
    if (wavesurfer && isReady) wavesurfer.playPause();
  }, [wavesurfer, isReady]);

  // ── Cleanup ──────────────────────────────────────────────────
  React.useEffect(() => {
    return () => { if (blobRef.current) URL.revokeObjectURL(blobRef.current); };
  }, []);

  // ── Error ────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="voice-error">
        {error}
        {isCloned && (
          <button
            onClick={generatePreview}
            style={{
              background: 'none', border: 'none', color: 'inherit',
              textDecoration: 'underline', cursor: 'pointer', padding: '0 0 0 8px',
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // ── Cloned — awaiting first generation ──────────────────────
  if (isCloned && !showPlayer) {
    return (
      <button
        className="voice-preview-trigger"
        onClick={generatePreview}
        disabled={isGenerating}
        style={{ width: '100%', marginTop: '8px' }}
      >
        {isGenerating
          ? <><span className="voice-spinner" /> Generating…</>
          : '🎧 Generate Preview'
        }
      </button>
    );
  }

  // ── Shared — awaiting first click ────────────────────────────
  if (!isCloned && !showPlayer) {
    return (
      <button
        className="voice-preview-trigger"
        onClick={handlePlayShared}
        disabled={!previewUrl}
        style={{ width: '100%', marginTop: '8px' }}
      >
        ▶ Preview
      </button>
    );
  }

  // ── Player ───────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      <WavesurferPlayer
        height={70}
        waveColor="rgba(136, 136, 168, 0.4)"
        progressColor="#7c3aed"
        cursorColor="#a78bfa"
        barWidth={2}
        barRadius={2}
        url={audioUrl}
        onReady={onReady}
        onPlay={onPlay}
        onPause={onPause}
        onFinish={onFinish}
      />

      <button
        className={`voice-preview-trigger voice-preview-trigger--play${!isReady ? ' voice-preview-trigger--loading' : ''}`}
        onClick={handlePlayPause}
        disabled={!isReady}
        style={{ width: '100%', marginTop: '8px' }}
        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
      >
        {!isReady
          ? <><span className="voice-spinner" /> Loading…</>
          : isPlaying ? '⏸ Pause' : '▶ Play'
        }
      </button>
    </div>
  );
};

export default Voice;